import React, { useState } from 'react';
import { BloodMarker, MarkerRecommendation } from '../../utils/api/natureDrApi';
import './MarkerTable.css';

interface MarkerTableProps {
  markers: BloodMarker[];
  recommendations?: MarkerRecommendation[];
}

const MarkerTable: React.FC<MarkerTableProps> = ({ markers, recommendations = [] }) => {
  const [expandedMarker, setExpandedMarker] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'abnormal'>('all');

  const filtered = filter === 'abnormal'
    ? markers.filter(m => m.status !== 'normal')
    : markers;

  const getRecommendation = (markerName: string) =>
    recommendations.find(r => r.markerName === markerName);

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'high': return 'status-high';
      case 'low': return 'status-low';
      default: return 'status-normal';
    }
  };

  const abnormalCount = markers.filter(m => m.status !== 'normal').length;

  return (
    <div className="marker-table-container">
      <div className="marker-table-header">
        <div className="marker-filter-tabs">
          <button
            className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All Markers ({markers.length})
          </button>
          <button
            className={`filter-tab ${filter === 'abnormal' ? 'active' : ''}`}
            onClick={() => setFilter('abnormal')}
          >
            Needs Attention ({abnormalCount})
          </button>
        </div>
      </div>

      <div className="marker-cards">
        {filtered.map((marker, index) => {
          const isExpanded = expandedMarker === marker.name;
          const recommendation = getRecommendation(marker.name);

          return (
            <div
              key={marker.name}
              className={`marker-card ${getStatusClass(marker.status)} ${isExpanded ? 'expanded' : ''}`}
              style={{ animationDelay: `${index * 0.05}s` }}
              onClick={() => setExpandedMarker(isExpanded ? null : marker.name)}
            >
              <div className="marker-card-main">
                <div className="marker-info">
                  <span className="marker-name">{marker.name}</span>
                  <span className="marker-range">
                    {marker.referenceMin !== null && marker.referenceMax !== null
                      ? `${marker.referenceMin} - ${marker.referenceMax} ${marker.unit}`
                      : marker.unit}
                  </span>
                </div>
                <div className="marker-value-section">
                  <span className={`marker-value ${getStatusClass(marker.status)}`}>
                    {marker.value}
                  </span>
                  <span className={`marker-status-badge ${getStatusClass(marker.status)}`}>
                    {marker.status === 'normal' ? (
                      <i className="fa-solid fa-check" />
                    ) : marker.status === 'high' ? (
                      <i className="fa-solid fa-arrow-up" />
                    ) : (
                      <i className="fa-solid fa-arrow-down" />
                    )}
                  </span>
                </div>
              </div>

              {recommendation && (
                <div className={`marker-recommendation ${isExpanded ? 'visible' : ''}`}>
                  <div className="recommendation-content">
                    <p className="recommendation-explanation">{recommendation.explanation}</p>

                    {recommendation.remedies.length > 0 && (
                      <div className="recommendation-remedies">
                        <h4>
                          <i className="fa-solid fa-leaf" /> Natural Remedies
                        </h4>
                        {recommendation.remedies.map((remedy, ri) => (
                          <div key={ri} className="remedy-item">
                            <span className="remedy-name">{remedy.name}</span>
                            <p className="remedy-description">{remedy.description}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {recommendation.dietaryAdvice && (
                      <div className="recommendation-diet">
                        <h4>
                          <i className="fa-solid fa-utensils" /> Dietary Advice
                        </h4>
                        <p>{recommendation.dietaryAdvice}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div className="marker-empty">
          <i className="fa-solid fa-check-circle" />
          <p>{filter === 'abnormal' ? 'All markers are within normal range!' : 'No markers found.'}</p>
        </div>
      )}
    </div>
  );
};

export default MarkerTable;
